import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { ArrowRight, Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import {
  fetchMeetingClients,
  createMeetingClient,
  updateMeetingClient,
  deleteMeetingClient,
} from "../services/clients.service";
import { Link } from "react-router-dom";
import Container from "../base/Container";
import { handleState } from "../functions";
import NoDataFound from "../components/common/NoDataFound";

const initialClientData = {
  _id: null,
  name: "",
  email: "",
  relationWith: "",
  relationType: "",
};

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentClient, setCurrentClient] = useState(initialClientData);
  const [isEditing, setIsEditing] = useState(false);
  const [subRowId, setSubRowId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const data: any = await fetchMeetingClients();
      setClients(data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };
  const handleOpenDialog = (client = initialClientData, edit = false) => {
    setCurrentClient(client);
    setIsEditing(edit);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentClient(initialClientData);
    setIsEditing(false);
  };
  const handleSaveClient = async () => {
    setLoading(true);
    if (isEditing && currentClient._id) {
      try {
        await updateMeetingClient(currentClient._id, currentClient);
        fetchClients();
      } catch (error) {
        console.error("Error updating client:", error);
      }
    } else if (!isEditing) {
      try {
        await createMeetingClient(currentClient);
        fetchClients(); // Refresh the data
      } catch (error) {
        console.error("Error adding client:", error);
      }
    }
    setLoading(false);
    handleCloseDialog();
  };
  const handleDeleteClient = async (_id) => {
    try {
      await deleteMeetingClient(_id);
      fetchClients(); // Refresh the data
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  return (
    <Container>
      <Box sx={{ padding: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <h2>Client Management</h2>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            Add Client
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ minHeight: 'calc(100vh - 200px)', position: 'relative' }}>
          {clients.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={100}>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <Fragment key={client._id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          onClick={() =>
                            setSubRowId(subRowId === client._id ? "" : client._id)
                          }
                        >
                          <ArrowRight
                            sx={{
                              transition: "rotate 0.2s ease-in-out",
                              rotate: subRowId === client._id ? "90deg" : "0deg",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>
                        <Link
                          to={`/client/${client._id}`}
                          onClick={() =>
                            localStorage.setItem("clientId", client._id)
                          }
                        >
                          <Tooltip title="View Client" arrow placement="top">
                            <IconButton color="primary" size="small">
                              <RemoveRedEye />
                            </IconButton>
                          </Tooltip>
                        </Link>
                        <Tooltip title="Edit Client" arrow placement="top">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog(client, true)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove Client" arrow placement="top">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClient(client._id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {subRowId === client._id &&
                      (client.relations.length !== 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Collapse in={subRowId === client._id}>
                              <Table
                                size="small"
                                sx={{
                                  border: "1px solid lightgray",
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Relationship</TableCell>
                                    <TableCell>Entity</TableCell>
                                    <TableCell>Bussiness</TableCell>
                                    <TableCell>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {/* {client.relation.length === 0} */}
                                  {client.relations.map((c) => (
                                    <TableRow>
                                      <TableCell>{c.name}</TableCell>
                                      <TableCell>
                                        {c.relationType || "N/A"}
                                      </TableCell>
                                      <TableCell>{c.entity || "N/A"}</TableCell>
                                      <TableCell>
                                        {c.bussiness || "N/A"}
                                      </TableCell>
                                      <TableCell>
                                        <Link
                                          to={`/client/${c._id}`}
                                          onClick={() =>
                                            localStorage.setItem(
                                              "clientId",
                                              c._id
                                            )
                                          }
                                        >
                                          <Tooltip
                                            title="View Client"
                                            arrow
                                            placement="top"
                                          >
                                            <IconButton
                                              color="primary"
                                              size="small"
                                            >
                                              <RemoveRedEye />
                                            </IconButton>
                                          </Tooltip>
                                        </Link>
                                        <Tooltip
                                          title="Edit Client"
                                          arrow
                                          placement="top"
                                        >
                                          <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() =>
                                              handleOpenDialog(c, true)
                                            }
                                          >
                                            <Edit />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title="Remove Client"
                                          arrow
                                          placement="top"
                                        >
                                          <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() =>
                                              handleDeleteClient(c._id)
                                            }
                                          >
                                            <Delete />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: "center" }}>
                            No Relative Client Exists!
                          </TableCell>
                        </TableRow>
                      ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
              }}
            >
              <NoDataFound
                title="No clients found"
                subtitle="No clients have been added yet. Create your first client to get started."
                imageWidth={150}
                imageHeight={150}
              />
            </Box>
          )}
        </TableContainer>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>{isEditing ? "Edit Client" : "Add Client"}</DialogTitle>
          <DialogContent>
            <TextField
              label="Name"
              value={currentClient.name}
              onChange={(e) =>
                handleState(setCurrentClient, e.target.value, "name")
              }
              fullWidth
              margin="dense"
            />
            <TextField
              label="Email"
              value={currentClient.email}
              onChange={(e) =>
                handleState(setCurrentClient, e.target.value, "email")
              }
              fullWidth
              margin="dense"
            />

            <FormControl sx={{ width: "100%", mt: 1 }}>
              <InputLabel>Select Clients</InputLabel>
              <Select
                value={currentClient.relationWith}
                onChange={(e) =>
                  handleState(setCurrentClient, e.target.value, "relationWith")
                }
                input={
                  <OutlinedInput
                    sx={{ textTransform: "capitalize" }}
                    label="Select Clients"
                  />
                }
                renderValue={(id) => {
                  const client = clients.find((c) => c._id === id);
                  return client ? client.name : id;
                }}
              >
                {clients
                  .filter((e) => e.email !== currentClient.email)
                  .map((client: any) => (
                    <MenuItem key={client._id} value={client._id}>
                      <ListItemText primary={client.name} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {currentClient.relationWith && (
              <TextField
                label="Relation Type"
                value={currentClient.relationType}
                onChange={(e) =>
                  handleState(setCurrentClient, e.target.value, "relationType")
                }
                fullWidth
                margin="dense"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSaveClient}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ClientManagement;
